import React from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router';
import * as yup from 'yup';

import { datasourceEndpoints } from '@/api/endpoints';
import { EditableText } from '@/components/atoms/EditableText/EditableText';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import useCollections from '@/hooks/useCollections';
import useDatasources from '@/hooks/useDatasources';
import { useFromPageState } from '@/hooks/useFromPageState';
import { selectAccountSlug } from '@/redux/session/selectors';
import { LENGTH_XS } from '@/util/validation/constants';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

export const ContentBreadcrumbs = () => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { collectionId, category, datasourceId } = useParams();
  const { collection } = useCollections(collectionId);
  const { datasource, updateDatasource } = useDatasources(collectionId, datasourceId);
  const previousPageIndex = useFromPageState();

  // Validation schema for datasource/folder name in breadcrumb
  const datasourceNameSchema = React.useMemo(() => {
    return yup
      .object()
      .shape({
        inputField: createNameFieldSchema({
          getEndpoint: (name) =>
            datasourceEndpoints.checkName(collectionId, name, { datasource_id: datasourceId }),
          max: LENGTH_XS,
          excludeName: datasource?.name,
        }),
      });
  }, [collectionId, datasourceId, datasource?.name]);

  const handleDatasourceNameSave = (newName: string) => {
    if (!datasource || !newName.trim() || newName === datasource.name) {
      return;
    }

    updateDatasource({
      ...datasource,
      name: newName.trim(),
      datasource_id: datasourceId,
      type: datasource.type,
    });
  };

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to={`/${slug}/knowledge-bases`} className="breadcrumb-link">
              {t('common.collections')}
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link
              to={`/${slug}/knowledge-bases/${collectionId}/category/${category}`}
              state={{ fromPage: previousPageIndex + 1 }}
              className="breadcrumb-link"
            >
              {collection?.name}
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem className="min-w-0 flex-1">
          <BreadcrumbPage className="min-w-0 border-0 p-0">
            <EditableText
              defaultValue={datasource?.name || ''}
              onSubmit={handleDatasourceNameSave}
              canEdit={true}
              variant="body-regular"
              placeholder={t('collections.folder_name')}
              validationSchema={datasourceNameSchema}
              overflowEllipsis
            />
          </BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
};
